import { useState, useEffect, useRef } from 'react';
import { Link, useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { motion } from 'framer-motion';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { useAuth } from '@/context/AuthContext';
import { jobsApi, quotesApi, paymentsApi, reviewsApi, questionsApi, messagesApi } from '@/lib/api';
import LiveTrackingMap from '@/components/LiveTrackingMap';
import { 
  Truck, 
  ArrowLeft, 
  MapPin, 
  Calendar,
  Package,
  DollarSign,
  Clock,
  Star,
  Loader2,
  CheckCircle,
  CreditCard,
  MessageSquare,
  User,
  AlertCircle,
  HelpCircle,
  Send,
  BadgeCheck,
  Navigation,
  Shield,
  AlertTriangle,
  Lock,
  Camera,
  MapPinned,
  UserCheck,
  XCircle,
  Upload
} from 'lucide-react';

const statusColors = {
  posted: 'bg-orange-500/15 text-orange-500',
  quoted: 'bg-blue-500/15 text-blue-500',
  accepted: 'bg-green-500/15 text-green-500',
  in_progress: 'bg-yellow-500/15 text-yellow-600',
  picked_up: 'bg-cyan-500/15 text-cyan-600',
  delivered: 'bg-indigo-500/15 text-indigo-600',
  awaiting_confirmation: 'bg-purple-500/15 text-purple-600',
  completed: 'bg-emerald-500/15 text-emerald-600',
  cancelled: 'bg-red-500/15 text-red-500',
  disputed: 'bg-red-500/15 text-red-500'
};

const quoteStatusColors = {
  pending: 'bg-yellow-500/15 text-yellow-600',
  accepted: 'bg-green-500/15 text-green-500',
  rejected: 'bg-red-500/15 text-red-500'
};

const QuoteCard = ({ quote, isOwner, onAccept, onPay, jobStatus, acceptedQuoteId }) => {
  const isAccepted = quote.id === acceptedQuoteId;
  
  return (
    <motion.div
      className={`quote-card ${isAccepted ? 'selected' : ''}`}
      initial={{ opacity: 0, y: 10 }}
      animate={{ opacity: 1, y: 0 }}
    >
      <div className="flex items-start justify-between mb-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-muted rounded-full flex items-center justify-center">
            <User className="w-5 h-5 text-muted-foreground" />
          </div>
          <div>
            <div className="flex items-center gap-2">
              <p className="font-medium">{quote.provider_name}</p>
              {quote.provider_verified && (
                <span 
                  className="inline-flex items-center gap-1 text-xs font-medium text-emerald-600 bg-emerald-500/10 px-1.5 py-0.5 rounded"
                  data-testid={`verified-badge-${quote.id}`}
                >
                  <BadgeCheck className="w-3 h-3" />
                  Verified
                </span>
              )}
            </div>
            <div className="flex items-center gap-1 text-sm">
              <Star className="w-3.5 h-3.5 text-yellow-500 fill-yellow-500" />
              <span>{quote.provider_rating?.toFixed(1) || '0.0'}</span>
              <span className="text-muted-foreground">({quote.provider_total_reviews || 0})</span>
            </div>
          </div>
        </div>
        <Badge className={`${quoteStatusColors[quote.status]} text-xs uppercase`}>
          {quote.status}
        </Badge>
      </div>
      
      <div className="mb-4">
        <p className="text-3xl font-bold price-display" style={{ fontFamily: 'Barlow Condensed' }}>
          A${quote.price.toFixed(2)}
        </p>
        <p className="text-xs text-muted-foreground mt-1">
          Total price - No hidden fees
        </p>
      </div>
      
      <div className="space-y-2 text-sm mb-4">
        <div className="flex items-center gap-2">
          <Clock className="w-4 h-4 text-muted-foreground" />
          <span>Pickup: {quote.estimated_pickup_time}</span>
        </div>
        <div className="flex items-center gap-2">
          <Calendar className="w-4 h-4 text-muted-foreground" />
          <span>Delivery: {quote.estimated_delivery_time}</span>
        </div>
        {quote.notes && (
          <div className="flex items-start gap-2 pt-2 border-t border-border">
            <MessageSquare className="w-4 h-4 text-muted-foreground mt-0.5" />
            <p className="text-muted-foreground italic">"{quote.notes}"</p>
          </div>
        )}
      </div>
      
      {isOwner && quote.status === 'pending' && jobStatus === 'quoted' && (
        <Button 
          className="w-full btn-active gap-2" 
          onClick={() => onAccept(quote)}
          data-testid={`accept-quote-${quote.id}`}
        >
          <CheckCircle className="w-4 h-4" />
          Accept Quote
        </Button>
      )}
      
      {isOwner && isAccepted && jobStatus === 'accepted' && (
        <Button 
          className="w-full btn-active gap-2" 
          onClick={() => onPay(quote)}
          data-testid={`pay-quote-${quote.id}`}
        >
          <CreditCard className="w-4 h-4" />
          Pay & Start Job
        </Button>
      )}
    </motion.div>
  );
};

export default function JobDetailPage() {
  const { jobId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [job, setJob] = useState(null);
  const [quotes, setQuotes] = useState([]);
  const [questions, setQuestions] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processingPayment, setProcessingPayment] = useState(false);
  const [reviewDialogOpen, setReviewDialogOpen] = useState(false);
  const [review, setReview] = useState({ rating: 5, comment: '' });
  const [submittingReview, setSubmittingReview] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [questionDialogOpen, setQuestionDialogOpen] = useState(false);
  const [newQuestion, setNewQuestion] = useState('');
  const [submittingQuestion, setSubmittingQuestion] = useState(false);
  const [answerDialogOpen, setAnswerDialogOpen] = useState(false);
  const [selectedQuestion, setSelectedQuestion] = useState(null);
  const [newAnswer, setNewAnswer] = useState('');
  const [submittingAnswer, setSubmittingAnswer] = useState(false);
  
  // Private messaging state (for paid jobs)
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [sendingMessage, setSendingMessage] = useState(false);
  const [messagingEnabled, setMessagingEnabled] = useState(false);
  const [canMessage, setCanMessage] = useState(false);
  
  // Delivery flow state
  const [markingPickedUp, setMarkingPickedUp] = useState(false);
  const [deliveryDialogOpen, setDeliveryDialogOpen] = useState(false);
  const [submittingDelivery, setSubmittingDelivery] = useState(false);
  const [deliveryProof, setDeliveryProof] = useState({
    photo_url: '',
    recipient_name: '',
    left_unattended: false,
    notes: ''
  });
  const [confirmingDelivery, setConfirmingDelivery] = useState(false);
  const [disputeDialogOpen, setDisputeDialogOpen] = useState(false);
  const [disputeReason, setDisputeReason] = useState('');
  const [submittingDispute, setSubmittingDispute] = useState(false);

  // Refs for deep linking scroll targets
  const confirmDeliveryRef = useRef(null);
  const quotesRef = useRef(null);
  const questionsRef = useRef(null);
  const trackingRef = useRef(null);

  // Handle URL hash for deep linking (scroll to section)
  useEffect(() => {
    if (!loading && job) {
      const hash = window.location.hash.replace('#', '');
      if (hash) {
        // Small delay to ensure DOM is ready
        setTimeout(() => {
          let targetRef = null;
          switch (hash) {
            case 'confirm-delivery':
              targetRef = confirmDeliveryRef;
              break;
            case 'quotes':
              targetRef = quotesRef;
              break;
            case 'questions':
              targetRef = questionsRef;
              break;
            case 'tracking':
              targetRef = trackingRef;
              break;
            default:
              break;
          }
          if (targetRef?.current) {
            targetRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
            // Flash highlight effect
            targetRef.current.classList.add('ring-2', 'ring-primary', 'ring-offset-2');
            setTimeout(() => {
              targetRef.current?.classList.remove('ring-2', 'ring-primary', 'ring-offset-2');
            }, 2000);
          }
        }, 300);
      }
    }
  }, [loading, job]);

  useEffect(() => {
    fetchData();
  }, [jobId]);

  const fetchData = async () => {
    try {
      const [jobRes, quotesRes, questionsRes] = await Promise.all([
        jobsApi.getById(jobId),
        quotesApi.getByJob(jobId),
        questionsApi.getByJob(jobId)
      ]);
      setJob(jobRes.data);
      setQuotes(quotesRes.data);
      setQuestions(questionsRes.data?.questions || []);
      
      // Fetch messages if job is paid
      try {
        const messagesRes = await messagesApi.getByJob(jobId);
        setMessages(messagesRes.data?.messages || []);
        setMessagingEnabled(messagesRes.data?.messaging_enabled || false);
        setCanMessage(messagesRes.data?.can_message || false);
      } catch (e) {
        // Messaging not available for this job yet
        setMessagingEnabled(false);
        setCanMessage(false);
      }
    } catch (error) {
      toast.error('Failed to load job details');
      navigate('/dashboard');
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptQuote = async (quote) => {
    setSelectedQuote(quote);
    setConfirmDialogOpen(true);
  };

  const confirmAcceptQuote = async () => {
    try {
      await quotesApi.accept(selectedQuote.id);
      toast.success('Quote accepted! Please proceed to payment.');
      setConfirmDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to accept quote');
    }
  };

  const handlePayment = async (quote) => {
    setProcessingPayment(true);
    try {
      const response = await paymentsApi.createCheckout({
        job_id: job.id,
        quote_id: quote.id,
        origin_url: window.location.origin
      });
      
      // Open Stripe checkout in new tab (avoids redirect blocking)
      const checkoutUrl = response.data.checkout_url;
      if (checkoutUrl) {
        window.open(checkoutUrl, '_blank');
        toast.success('Payment page opened in new tab. Complete payment there.');
      } else {
        toast.error('No checkout URL received');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create checkout');
    } finally {
      setProcessingPayment(false);
    }
  };

  const handleCompleteJob = async () => {
    try {
      // Find the payment for this job
      const acceptedQuote = quotes.find(q => q.id === job.accepted_quote_id);
      if (!acceptedQuote) {
        toast.error('No accepted quote found');
        return;
      }
      
      await jobsApi.updateStatus(job.id, 'completed');
      toast.success('Job marked as completed!');
      setReviewDialogOpen(true);
      fetchData();
    } catch (error) {
      toast.error('Failed to complete job');
    }
  };

  // Enhanced delivery flow handlers
  const handleMarkPickedUp = async () => {
    setMarkingPickedUp(true);
    try {
      await jobsApi.markPickedUp(job.id);
      toast.success('Load marked as picked up! Location sharing enabled.');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to mark as picked up');
    } finally {
      setMarkingPickedUp(false);
    }
  };

  const handleMarkDelivered = async () => {
    if (!deliveryProof.photo_url) {
      toast.error('Please upload a photo of the delivery');
      return;
    }
    
    setSubmittingDelivery(true);
    try {
      // Get current location
      const position = await new Promise((resolve, reject) => {
        navigator.geolocation.getCurrentPosition(resolve, reject, {
          enableHighAccuracy: true,
          timeout: 10000
        });
      });
      
      await jobsApi.markDelivered(job.id, {
        photo_url: deliveryProof.photo_url,
        delivery_latitude: position.coords.latitude,
        delivery_longitude: position.coords.longitude,
        recipient_name: deliveryProof.recipient_name || null,
        left_unattended: deliveryProof.left_unattended,
        notes: deliveryProof.notes || null
      });
      
      toast.success('Delivery submitted! Awaiting customer confirmation.');
      setDeliveryDialogOpen(false);
      fetchData();
    } catch (error) {
      if (error.code === 1) {
        toast.error('Location access denied. Please enable location services.');
      } else {
        toast.error(error.response?.data?.detail || 'Failed to submit delivery');
      }
    } finally {
      setSubmittingDelivery(false);
    }
  };

  const handleConfirmDelivery = async () => {
    setConfirmingDelivery(true);
    try {
      await jobsApi.confirmDelivery(job.id);
      toast.success('Delivery confirmed! Payment released to provider.');
      setReviewDialogOpen(true);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to confirm delivery');
    } finally {
      setConfirmingDelivery(false);
    }
  };

  const handleDispute = async () => {
    if (!disputeReason.trim()) {
      toast.error('Please provide a reason for the dispute');
      return;
    }
    
    setSubmittingDispute(true);
    try {
      await jobsApi.disputeDelivery(job.id, { reason: disputeReason });
      toast.success('Dispute submitted. An admin will review your case.');
      setDisputeDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit dispute');
    } finally {
      setSubmittingDispute(false);
    }
  };

  const handlePhotoUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;
    
    // For now, create a simple URL - in production, upload to cloud storage
    const formData = new FormData();
    formData.append('file', file);
    
    try {
      const API_URL = process.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${API_URL}/api/upload`, {
        method: 'POST',
        body: formData
      });
      const data = await response.json();
      setDeliveryProof(prev => ({ ...prev, photo_url: data.url }));
      toast.success('Photo uploaded!');
    } catch (error) {
      // Fallback: use local URL for preview
      const localUrl = URL.createObjectURL(file);
      setDeliveryProof(prev => ({ ...prev, photo_url: localUrl }));
      toast.success('Photo ready!');
    }
  };

  const handleSubmitReview = async () => {
    if (!review.comment.trim()) {
      toast.error('Please write a review comment');
      return;
    }
    
    setSubmittingReview(true);
    try {
      await reviewsApi.create({
        job_id: job.id,
        provider_id: job.accepted_provider_id,
        rating: review.rating,
        comment: review.comment
      });
      toast.success('Review submitted! Thank you for your feedback.');
      setReviewDialogOpen(false);
      navigate('/dashboard');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit review');
    } finally {
      setSubmittingReview(false);
    }
  };

  const handleAskQuestion = async () => {
    if (!newQuestion.trim()) {
      toast.error('Please enter your question');
      return;
    }
    
    setSubmittingQuestion(true);
    try {
      const response = await questionsApi.ask(jobId, newQuestion);
      if (response.data?.flagged) {
        toast.warning('Your question has been submitted for review. It may contain content that needs approval before being visible to others.');
      } else {
        toast.success('Question submitted! The customer will be notified.');
      }
      setQuestionDialogOpen(false);
      setNewQuestion('');
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit question');
    } finally {
      setSubmittingQuestion(false);
    }
  };

  const handleAnswerQuestion = async () => {
    if (!newAnswer.trim()) {
      toast.error('Please enter your answer');
      return;
    }
    
    setSubmittingAnswer(true);
    try {
      const response = await questionsApi.answer(jobId, selectedQuestion.id, newAnswer);
      if (response.data?.flagged) {
        toast.warning('Your answer has been submitted for review. It may contain content that needs approval before being visible to others.');
      } else {
        toast.success('Answer submitted! The provider will be notified.');
      }
      setAnswerDialogOpen(false);
      setNewAnswer('');
      setSelectedQuestion(null);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to submit answer');
    } finally {
      setSubmittingAnswer(false);
    }
  };

  const handleSendMessage = async () => {
    if (!newMessage.trim()) {
      toast.error('Please enter a message');
      return;
    }
    
    setSendingMessage(true);
    try {
      await messagesApi.send(jobId, newMessage);
      setNewMessage('');
      // Refresh messages
      const messagesRes = await messagesApi.getByJob(jobId);
      setMessages(messagesRes.data?.messages || []);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to send message');
    } finally {
      setSendingMessage(false);
    }
  };

  const openAnswerDialog = (question) => {
    setSelectedQuestion(question);
    setNewAnswer('');
    setAnswerDialogOpen(true);
  };

  const isOwner = user?.id === job?.customer_id;
  const isProvider = user?.role === 'provider';
  const isAssignedProvider = user?.id === job?.accepted_provider_id;

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <Loader2 className="w-8 h-8 animate-spin text-primary" />
      </div>
    );
  }

  if (!job) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <p>Job not found</p>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="sticky top-0 z-50 glass border-b border-border">
        <div className="container-app py-4 flex items-center justify-between">
          <Link 
            to={isProvider ? '/provider' : '/dashboard'} 
            className="flex items-center gap-2 text-muted-foreground hover:text-foreground"
          >
            <ArrowLeft className="w-5 h-5" />
            <span className="hidden sm:inline">Back to Dashboard</span>
          </Link>
          <Link to="/" className="flex items-center gap-2">
            <div className="w-8 h-8 bg-primary rounded-sm flex items-center justify-center">
              <Truck className="w-5 h-5 text-white" />
            </div>
            <span className="font-bold text-sm" style={{ fontFamily: 'Barlow Condensed' }}>
              THM
            </span>
          </Link>
        </div>
      </header>

      <main className="container-app py-8">
        <div className="grid lg:grid-cols-3 gap-8">
          {/* Job Details */}
          <div className="lg:col-span-2 space-y-6">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <div className="flex items-start justify-between mb-4">
                <div>
                  <h1 className="text-3xl font-bold mb-2" style={{ fontFamily: 'Barlow Condensed' }}>
                    {job.title}
                  </h1>
                  <div className="flex items-center gap-3">
                    <Badge className={`${statusColors[job.status]} uppercase`}>
                      {job.status.replace('_', ' ')}
                    </Badge>
                    <span className="text-sm text-muted-foreground">
                      Posted {new Date(job.created_at).toLocaleDateString()}
                    </span>
                  </div>
                </div>
              </div>
              
              <p className="text-muted-foreground mb-6">{job.description}</p>

              {/* Route Card */}
              <div className="bg-card border border-border rounded-sm p-6 mb-6">
                <h3 className="font-semibold mb-4 flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed' }}>
                  <MapPin className="w-5 h-5 text-primary" />
                  ROUTE DETAILS
                </h3>
                
                <div className="route-line">
                  <div className="route-point">
                    <p className="font-medium">{job.pickup_city}, {job.pickup_state}</p>
                    <p className="text-sm text-muted-foreground">{job.pickup_location}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Pickup: {new Date(job.pickup_date).toLocaleDateString()}
                    </p>
                  </div>
                  <div className="route-point destination">
                    <p className="font-medium">{job.delivery_city}, {job.delivery_state}</p>
                    <p className="text-sm text-muted-foreground">{job.delivery_location}</p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Deadline: {new Date(job.delivery_deadline).toLocaleDateString()}
                    </p>
                  </div>
                </div>
              </div>

              {/* Load Details */}
              <div className="bg-card border border-border rounded-sm p-6">
                <h3 className="font-semibold mb-4 flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed' }}>
                  <Package className="w-5 h-5 text-primary" />
                  LOAD DETAILS
                </h3>
                
                <div className="grid sm:grid-cols-2 gap-4">
                  <div>
                    <p className="text-sm text-muted-foreground">Load Type</p>
                    <p className="font-medium">{job.load_type}</p>
                  </div>
                  <div>
                    <p className="text-sm text-muted-foreground">Weight</p>
                    <p className="font-medium">{job.weight_kg?.toLocaleString()} kg</p>
                  </div>
                  {job.dimensions && (
                    <div>
                      <p className="text-sm text-muted-foreground">Dimensions</p>
                      <p className="font-medium">{job.dimensions}</p>
                    </div>
                  )}
                  {job.special_requirements && (
                    <div className="sm:col-span-2">
                      <p className="text-sm text-muted-foreground">Special Requirements</p>
                      <p className="font-medium">{job.special_requirements}</p>
                    </div>
                  )}
                  {job.authority_to_leave && (
                    <div className="sm:col-span-2 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                      <p className="text-sm font-medium text-yellow-800 flex items-center gap-2">
                        <span>✓</span> Authority to Leave Unattended
                      </p>
                      {job.atl_instructions && (
                        <p className="text-sm text-yellow-700 mt-1">
                          <span className="font-medium">Instructions:</span> {job.atl_instructions}
                        </p>
                      )}
                    </div>
                  )}
                </div>
              </div>

              {/* Live Tracking Map (for picked_up jobs) */}
              {['in_progress', 'accepted', 'picked_up'].includes(job.status) && job.accepted_provider_id && (
                <div ref={trackingRef} className="mt-6 transition-all duration-300">
                  <LiveTrackingMap 
                    jobId={job.id} 
                    isProvider={isProvider && job.accepted_provider_id === user?.id}
                  />
                </div>
              )}

              {/* PROVIDER: Mark as Picked Up Button */}
              {isAssignedProvider && ['in_progress', 'accepted'].includes(job.status) && (
                <div className="bg-card border border-cyan-500/30 rounded-sm p-6 mt-6">
                  <div className="flex items-center gap-3 mb-4">
                    <Truck className="w-5 h-5 text-cyan-500" />
                    <p className="font-medium">Ready to Pick Up?</p>
                  </div>
                  <p className="text-sm text-muted-foreground mb-4">
                    Mark the load as picked up when you have it. This will notify the customer and enable live tracking.
                  </p>
                  <Button 
                    className="w-full bg-cyan-500 hover:bg-cyan-600 gap-2"
                    onClick={handleMarkPickedUp}
                    disabled={markingPickedUp}
                    data-testid="mark-picked-up-btn"
                  >
                    {markingPickedUp ? (
                      <Loader2 className="w-4 h-4 animate-spin" />
                    ) : (
                      <Package className="w-4 h-4" />
                    )}
                    Mark as Picked Up
                  </Button>
                </div>
              )}

              {/* PROVIDER: Mark as Delivered Button */}
              {isAssignedProvider && job.status === 'picked_up' && (
                <div className="bg-card border border-green-500/30 rounded-sm p-6 mt-6">
                  <div className="flex items-center gap-3 mb-4">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <p className="font-medium">Delivery Complete?</p>
                  </div>
                  <p className="text-sm text-muted-foreground mb-4">
                    When you arrive at the delivery location, mark as delivered and provide proof of delivery.
                  </p>
                  <Button 
                    className="w-full bg-green-500 hover:bg-green-600 gap-2"
                    onClick={() => setDeliveryDialogOpen(true)}
                    data-testid="mark-delivered-btn"
                  >
                    <MapPinned className="w-4 h-4" />
                    Mark as Delivered
                  </Button>
                </div>
              )}

              {/* CUSTOMER: Confirm or Dispute Delivery */}
              {isOwner && job.status === 'awaiting_confirmation' && (
                <div ref={confirmDeliveryRef} className="bg-card border border-purple-500/30 rounded-sm p-6 mt-6 transition-all duration-300">
                  <div className="flex items-center gap-3 mb-4">
                    <AlertCircle className="w-5 h-5 text-purple-500" />
                    <p className="font-medium">Delivery Awaiting Your Confirmation</p>
                  </div>
                  
                  {/* Delivery Proof Display */}
                  {job.delivery_proof && (
                    <div className="mb-4 p-4 bg-muted/30 rounded-sm">
                      <p className="text-sm font-medium mb-2">Delivery Proof:</p>
                      {job.delivery_proof.photo_url && (
                        <img 
                          src={job.delivery_proof.photo_url} 
                          alt="Delivery proof" 
                          className="w-full max-w-sm rounded mb-2"
                        />
                      )}
                      <div className="text-sm space-y-1">
                        {job.delivery_proof.recipient_name && (
                          <p><strong>Received by:</strong> {job.delivery_proof.recipient_name}</p>
                        )}
                        {job.delivery_proof.left_unattended && (
                          <p className="text-yellow-600">Left unattended (authority given)</p>
                        )}
                        {job.delivery_proof.notes && (
                          <p><strong>Notes:</strong> {job.delivery_proof.notes}</p>
                        )}
                      </div>
                    </div>
                  )}
                  
                  <p className="text-sm text-muted-foreground mb-4">
                    Please confirm the delivery was received correctly. If there are issues, you can dispute within 24 hours.
                    {job.confirmation_deadline && (
                      <span className="block mt-1 text-yellow-600">
                        Auto-confirms: {new Date(job.confirmation_deadline).toLocaleString()}
                      </span>
                    )}
                  </p>
                  
                  <div className="flex gap-3">
                    <Button 
                      className="flex-1 bg-green-500 hover:bg-green-600 gap-2"
                      onClick={handleConfirmDelivery}
                      disabled={confirmingDelivery}
                      data-testid="confirm-delivery-btn"
                    >
                      {confirmingDelivery ? (
                        <Loader2 className="w-4 h-4 animate-spin" />
                      ) : (
                        <CheckCircle className="w-4 h-4" />
                      )}
                      Confirm Delivery
                    </Button>
                    <Button 
                      variant="outline"
                      className="flex-1 border-red-500 text-red-500 hover:bg-red-500/10 gap-2"
                      onClick={() => setDisputeDialogOpen(true)}
                      data-testid="dispute-btn"
                    >
                      <XCircle className="w-4 h-4" />
                      Dispute
                    </Button>
                  </div>
                </div>
              )}

              {/* Completed Job - Review Prompt */}
              {isOwner && job.status === 'completed' && (
                <div className="bg-card border border-green-500/30 rounded-sm p-6 mt-6">
                  <div className="flex items-center gap-3 mb-4">
                    <CheckCircle className="w-5 h-5 text-green-500" />
                    <p className="font-medium">Job Completed!</p>
                  </div>
                  
                  {/* Show Delivery Proof for completed jobs */}
                  {job.delivery_proof && (
                    <div className="mb-4 p-4 bg-muted/30 rounded-sm">
                      <p className="text-sm font-medium mb-2">Delivery Proof:</p>
                      {job.delivery_proof.photo_url && (
                        <img 
                          src={job.delivery_proof.photo_url} 
                          alt="Delivery proof" 
                          className="w-full max-w-sm rounded mb-2 cursor-pointer hover:opacity-90"
                          onClick={() => window.open(job.delivery_proof.photo_url, '_blank')}
                        />
                      )}
                      <div className="text-sm space-y-1">
                        {job.delivery_proof.recipient_name && (
                          <p><strong>Received by:</strong> {job.delivery_proof.recipient_name}</p>
                        )}
                        {job.delivery_proof.left_unattended && (
                          <p className="text-yellow-600">Left unattended (authority given)</p>
                        )}
                        {job.delivery_proof.notes && (
                          <p><strong>Notes:</strong> {job.delivery_proof.notes}</p>
                        )}
                        {job.delivery_proof.submitted_at && (
                          <p className="text-muted-foreground text-xs mt-2">
                            Delivered: {new Date(job.delivery_proof.submitted_at).toLocaleString()}
                          </p>
                        )}
                      </div>
                    </div>
                  )}
                  
                  <p className="text-sm text-muted-foreground mb-4">
                    Payment has been released to the provider. Please leave a review for your experience.
                  </p>
                  <Button 
                    className="w-full btn-active gap-2"
                    onClick={() => setReviewDialogOpen(true)}
                    data-testid="leave-review-btn"
                  >
                    <Star className="w-4 h-4" />
                    Leave a Review
                  </Button>
                </div>
              )}

              {/* Questions Section - Public Q&A with Moderation */}
              <div ref={questionsRef} className="bg-card border border-border rounded-sm p-6 mt-6 transition-all duration-300">
                <div className="flex items-center justify-between mb-4">
                  <h3 className="font-semibold flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed' }}>
                    <HelpCircle className="w-5 h-5 text-primary" />
                    QUESTIONS ({questions.filter(q => q.moderation_status !== 'pending_review' || q.provider_id === user?.id).length})
                  </h3>
                  {isProvider && ['posted', 'quoted'].includes(job.status) && (
                    <Button 
                      variant="outline" 
                      size="sm" 
                      className="gap-2"
                      onClick={() => setQuestionDialogOpen(true)}
                      data-testid="ask-question-btn"
                    >
                      <HelpCircle className="w-4 h-4" />
                      Ask Question
                    </Button>
                  )}
                </div>
                
                <div className="text-xs text-muted-foreground mb-4 p-2 bg-muted/30 rounded flex items-center gap-2">
                  <Shield className="w-4 h-4" />
                  Questions and answers are moderated to ensure platform safety. Contact information sharing is not permitted.
                </div>
                
                {questions.length === 0 ? (
                  <p className="text-sm text-muted-foreground text-center py-4">
                    No questions yet. {isProvider ? 'Ask a question before quoting.' : 'Providers can ask questions about this job.'}
                  </p>
                ) : (
                  <div className="space-y-4">
                    {questions.map((q) => (
                      <div key={q.id} className={`border rounded-sm p-4 ${q.moderation_status === 'pending_review' ? 'border-yellow-500/50 bg-yellow-500/5' : 'border-border'}`}>
                        <div className="flex items-start gap-3 mb-2">
                          <div className={`w-8 h-8 rounded-full flex items-center justify-center flex-shrink-0 ${q.moderation_status === 'pending_review' ? 'bg-yellow-500/10' : 'bg-blue-500/10'}`}>
                            {q.moderation_status === 'pending_review' ? (
                              <AlertTriangle className="w-4 h-4 text-yellow-500" />
                            ) : (
                              <HelpCircle className="w-4 h-4 text-blue-500" />
                            )}
                          </div>
                          <div className="flex-1">
                            <div className="flex items-center gap-2 mb-1 flex-wrap">
                              <span className="text-sm font-medium">{q.provider_name}</span>
                              <span className="text-xs text-muted-foreground">
                                {new Date(q.created_at).toLocaleDateString('en-AU')}
                              </span>
                              {q.moderation_status === 'pending_review' && q.provider_id === user?.id && (
                                <span className="text-xs bg-yellow-500/20 text-yellow-600 px-2 py-0.5 rounded">
                                  Pending Review
                                </span>
                              )}
                            </div>
                            <p className="text-sm">{q.question}</p>
                          </div>
                        </div>
                        
                        {q.answer ? (
                          <div className={`ml-11 mt-3 p-3 rounded-sm ${q.answer_hidden ? 'bg-yellow-500/10 border border-yellow-500/30' : 'bg-muted/50'}`}>
                            <div className="flex items-center gap-2 mb-1">
                              <span className="text-xs font-medium text-primary">Customer Answer</span>
                              {q.answer_hidden ? (
                                <span className="text-xs bg-yellow-500/20 text-yellow-600 px-2 py-0.5 rounded">
                                  Under Review
                                </span>
                              ) : (
                                <span className="text-xs text-muted-foreground">
                                  {q.answered_at && new Date(q.answered_at).toLocaleDateString('en-AU')}
                                </span>
                              )}
                            </div>
                            <p className="text-sm">{q.answer}</p>
                          </div>
                        ) : isOwner && q.moderation_status === 'approved' ? (
                          <div className="ml-11 mt-2">
                            <Button 
                              variant="outline" 
                              size="sm"
                              onClick={() => openAnswerDialog(q)}
                              data-testid={`answer-question-${q.id}`}
                            >
                              Answer Question
                            </Button>
                          </div>
                        ) : q.moderation_status === 'pending_review' ? (
                          <p className="ml-11 mt-2 text-xs text-yellow-600 italic">
                            Question is pending moderation review...
                          </p>
                        ) : (
                          <p className="ml-11 mt-2 text-xs text-muted-foreground italic">
                            Awaiting customer response...
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </div>

              {/* Private Messaging Section - Only for Paid Jobs */}
              {messagingEnabled && (
                <div className="bg-card border border-border rounded-sm p-6 mt-6" data-testid="job-messages-section">
                  <div className="flex items-center justify-between mb-4">
                    <h3 className="font-semibold flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed' }}>
                      <MessageSquare className="w-5 h-5 text-primary" />
                      PRIVATE MESSAGES
                    </h3>
                    <span className="text-xs text-emerald-600 bg-emerald-500/10 px-2 py-1 rounded flex items-center gap-1">
                      <Lock className="w-3 h-3" />
                      Payment Secured
                    </span>
                  </div>
                  
                  <div className="text-xs text-muted-foreground mb-4 p-2 bg-emerald-500/5 rounded border border-emerald-500/20 flex items-center gap-2">
                    <CheckCircle className="w-4 h-4 text-emerald-500" />
                    Payment has been made. You can now freely communicate with {isOwner ? 'the provider' : 'the customer'}.
                  </div>
                  
                  {/* Messages List */}
                  <div className="space-y-3 max-h-96 overflow-y-auto mb-4">
                    {messages.length === 0 ? (
                      <p className="text-sm text-muted-foreground text-center py-4">
                        No messages yet. Start a conversation!
                      </p>
                    ) : (
                      messages.map((msg) => (
                        <div 
                          key={msg.id} 
                          className={`p-3 rounded-sm ${msg.sender_id === user?.id ? 'bg-primary/10 ml-8' : 'bg-muted/50 mr-8'}`}
                        >
                          <div className="flex items-center gap-2 mb-1">
                            <span className="text-xs font-medium">
                              {msg.sender_id === user?.id ? 'You' : msg.sender_name}
                            </span>
                            <span className="text-xs text-muted-foreground">
                              {new Date(msg.created_at).toLocaleString('en-AU', { dateStyle: 'short', timeStyle: 'short' })}
                            </span>
                          </div>
                          <p className="text-sm">{msg.message}</p>
                        </div>
                      ))
                    )}
                  </div>
                  
                  {/* Send Message Input */}
                  {canMessage && (
                    <div className="flex gap-2">
                      <Textarea
                        placeholder="Type your message..."
                        value={newMessage}
                        onChange={(e) => setNewMessage(e.target.value)}
                        className="min-h-[60px] resize-none"
                        data-testid="message-input"
                      />
                      <Button 
                        onClick={handleSendMessage}
                        disabled={sendingMessage || !newMessage.trim()}
                        className="btn-active"
                        data-testid="send-message-btn"
                      >
                        {sendingMessage ? (
                          <Loader2 className="w-4 h-4 animate-spin" />
                        ) : (
                          <Send className="w-4 h-4" />
                        )}
                      </Button>
                    </div>
                  )}
                </div>
              )}
            </motion.div>
          </div>

          {/* Quotes Sidebar */}
          <div ref={quotesRef} className="space-y-4 transition-all duration-300">
            <div className="flex items-center justify-between">
              <h2 className="text-xl font-bold" style={{ fontFamily: 'Barlow Condensed' }}>
                QUOTES ({quotes.length}/5)
              </h2>
            </div>

            {quotes.length === 0 ? (
              <div className="bg-card border border-border rounded-sm p-8 text-center">
                <div className="w-12 h-12 bg-muted rounded-sm flex items-center justify-center mx-auto mb-4">
                  <DollarSign className="w-6 h-6 text-muted-foreground" />
                </div>
                <p className="text-muted-foreground">
                  No quotes yet. Providers have been notified.
                </p>
              </div>
            ) : (
              <div className="space-y-4">
                {quotes.map((quote) => (
                  <QuoteCard
                    key={quote.id}
                    quote={quote}
                    isOwner={isOwner}
                    onAccept={handleAcceptQuote}
                    onPay={handlePayment}
                    jobStatus={job.status}
                    acceptedQuoteId={job.accepted_quote_id}
                  />
                ))}
              </div>
            )}
          </div>
        </div>
      </main>

      {/* Confirm Accept Dialog */}
      <Dialog open={confirmDialogOpen} onOpenChange={setConfirmDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Barlow Condensed' }}>
              ACCEPT THIS QUOTE?
            </DialogTitle>
            <DialogDescription>
              You're about to accept the quote from {selectedQuote?.provider_name} for ${selectedQuote?.price.toFixed(2)}.
              Other quotes will be rejected.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmDialogOpen(false)}>
              Cancel
            </Button>
            <Button className="btn-active" onClick={confirmAcceptQuote}>
              Accept Quote
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Review Dialog */}
      <Dialog open={reviewDialogOpen} onOpenChange={setReviewDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Barlow Condensed' }}>
              LEAVE A REVIEW
            </DialogTitle>
            <DialogDescription>
              How was your experience with this provider?
            </DialogDescription>
          </DialogHeader>
          
          <div className="space-y-4 py-4">
            <div className="space-y-2">
              <Label>Rating</Label>
              <div className="flex gap-2">
                {[1, 2, 3, 4, 5].map((star) => (
                  <button
                    key={star}
                    type="button"
                    onClick={() => setReview({ ...review, rating: star })}
                    className="p-1"
                    data-testid={`star-${star}`}
                  >
                    <Star 
                      className={`w-8 h-8 ${star <= review.rating ? 'text-yellow-500 fill-yellow-500' : 'text-gray-300'}`}
                    />
                  </button>
                ))}
              </div>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="comment">Your Review</Label>
              <Textarea
                id="comment"
                placeholder="Share your experience..."
                value={review.comment}
                onChange={(e) => setReview({ ...review, comment: e.target.value })}
                rows={4}
                data-testid="review-comment-input"
              />
            </div>
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setReviewDialogOpen(false)}>
              Skip
            </Button>
            <Button 
              className="btn-active" 
              onClick={handleSubmitReview}
              disabled={submittingReview}
              data-testid="submit-review-btn"
            >
              {submittingReview ? (
                <>
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  Submitting...
                </>
              ) : (
                'Submit Review'
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Payment Processing Overlay */}
      {processingPayment && (
        <div className="fixed inset-0 bg-background/80 backdrop-blur-sm flex items-center justify-center z-50">
          <div className="text-center">
            <Loader2 className="w-12 h-12 animate-spin text-primary mx-auto mb-4" />
            <p className="text-lg font-medium">Redirecting to payment...</p>
          </div>
        </div>
      )}

      {/* Ask Question Dialog */}
      <Dialog open={questionDialogOpen} onOpenChange={setQuestionDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Barlow Condensed' }}>
              ASK A QUESTION
            </DialogTitle>
            <DialogDescription>
              Ask the customer a question about this job before submitting your quote.
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <Textarea
              placeholder="Type your question here..."
              value={newQuestion}
              onChange={(e) => setNewQuestion(e.target.value)}
              rows={4}
              data-testid="question-input"
            />
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setQuestionDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              className="btn-active gap-2" 
              onClick={handleAskQuestion}
              disabled={submittingQuestion}
              data-testid="submit-question-btn"
            >
              {submittingQuestion ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Send Question
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Answer Question Dialog */}
      <Dialog open={answerDialogOpen} onOpenChange={setAnswerDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle style={{ fontFamily: 'Barlow Condensed' }}>
              ANSWER QUESTION
            </DialogTitle>
            <DialogDescription>
              {selectedQuestion?.provider_name} asked: "{selectedQuestion?.question}"
            </DialogDescription>
          </DialogHeader>
          
          <div className="py-4">
            <Textarea
              placeholder="Type your answer here..."
              value={newAnswer}
              onChange={(e) => setNewAnswer(e.target.value)}
              rows={4}
              data-testid="answer-input"
            />
          </div>
          
          <DialogFooter>
            <Button variant="outline" onClick={() => setAnswerDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              className="btn-active gap-2" 
              onClick={handleAnswerQuestion}
              disabled={submittingAnswer}
              data-testid="submit-answer-btn"
            >
              {submittingAnswer ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin" />
                  Sending...
                </>
              ) : (
                <>
                  <Send className="w-4 h-4" />
                  Send Answer
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Delivery Proof Dialog */}
      <Dialog open={deliveryDialogOpen} onOpenChange={setDeliveryDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Barlow Condensed' }}>
              <Camera className="w-5 h-5 text-green-500" />
              SUBMIT DELIVERY PROOF
            </DialogTitle>
            <DialogDescription>
              Please provide proof of delivery. Photo is required.
            </DialogDescription>
          </DialogHeader>

          <div className="space-y-4 py-4">
            {/* Photo Upload */}
            <div className="space-y-2">
              <label className="text-sm font-medium">
                Delivery Photo <span className="text-red-500">*</span>
              </label>
              {deliveryProof.photo_url ? (
                <div className="relative">
                  <img 
                    src={deliveryProof.photo_url} 
                    alt="Delivery proof" 
                    className="w-full rounded-sm max-h-48 object-cover"
                  />
                  <Button
                    variant="outline"
                    size="sm"
                    className="absolute top-2 right-2"
                    onClick={() => setDeliveryProof(prev => ({ ...prev, photo_url: '' }))}
                  >
                    Change
                  </Button>
                </div>
              ) : (
                <label className="flex flex-col items-center justify-center w-full h-32 border-2 border-dashed border-border rounded-sm cursor-pointer hover:bg-muted/50">
                  <Upload className="w-8 h-8 text-muted-foreground mb-2" />
                  <span className="text-sm text-muted-foreground">Click to upload photo</span>
                  <input
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={handlePhotoUpload}
                    capture="environment"
                  />
                </label>
              )}
            </div>

            {/* Left Unattended Checkbox */}
            {job?.authority_to_leave && (
              <div className="space-y-2">
                <label className="flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={deliveryProof.left_unattended}
                    onChange={(e) => setDeliveryProof(prev => ({ 
                      ...prev, 
                      left_unattended: e.target.checked,
                      recipient_name: e.target.checked ? '' : prev.recipient_name
                    }))}
                    className="rounded"
                  />
                  <span className="text-sm">Left unattended (authority given by customer)</span>
                </label>
                {job?.atl_instructions && (
                  <div className="ml-6 p-3 bg-yellow-50 border border-yellow-200 rounded-lg">
                    <p className="text-sm font-medium text-yellow-800">Customer Instructions:</p>
                    <p className="text-sm text-yellow-700 mt-1">{job.atl_instructions}</p>
                  </div>
                )}
              </div>
            )}

            {/* Recipient Name */}
            {!deliveryProof.left_unattended && (
              <div className="space-y-2">
                <label className="text-sm font-medium">Recipient Name</label>
                <Input
                  placeholder="Name of person who received the delivery"
                  value={deliveryProof.recipient_name}
                  onChange={(e) => setDeliveryProof(prev => ({ ...prev, recipient_name: e.target.value }))}
                />
              </div>
            )}

            {/* Notes */}
            <div className="space-y-2">
              <label className="text-sm font-medium">Additional Notes (optional)</label>
              <Input
                placeholder="Any additional delivery notes..."
                value={deliveryProof.notes}
                onChange={(e) => setDeliveryProof(prev => ({ ...prev, notes: e.target.value }))}
              />
            </div>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDeliveryDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              className="bg-green-500 hover:bg-green-600 gap-2"
              onClick={handleMarkDelivered}
              disabled={submittingDelivery || !deliveryProof.photo_url}
            >
              {submittingDelivery ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <CheckCircle className="w-4 h-4" />
              )}
              Submit Delivery
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Dispute Dialog */}
      <Dialog open={disputeDialogOpen} onOpenChange={setDisputeDialogOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-500" style={{ fontFamily: 'Barlow Condensed' }}>
              <AlertTriangle className="w-5 h-5" />
              DISPUTE DELIVERY
            </DialogTitle>
            <DialogDescription>
              Please explain why you're disputing this delivery. An admin will review your case.
            </DialogDescription>
          </DialogHeader>

          <div className="py-4">
            <textarea
              className="w-full min-h-[120px] p-3 border border-border rounded-sm bg-background resize-none focus:outline-none focus:ring-2 focus:ring-primary"
              placeholder="Describe the issue with this delivery..."
              value={disputeReason}
              onChange={(e) => setDisputeReason(e.target.value)}
            />
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDisputeDialogOpen(false)}>
              Cancel
            </Button>
            <Button 
              variant="destructive"
              className="gap-2"
              onClick={handleDispute}
              disabled={submittingDispute || !disputeReason.trim()}
            >
              {submittingDispute ? (
                <Loader2 className="w-4 h-4 animate-spin" />
              ) : (
                <XCircle className="w-4 h-4" />
              )}
              Submit Dispute
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
